import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Landmark } from "lucide-react";
import {
  BANK_ACCOUNT_ROUTES,
  bankAccountResponseSchema,
  bankAccountSchema,
  usBankAccountSchema,
  type BankAccount,
  type UsBankAccount,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Input, Textarea } from "@/shared/ui/core";
import { CredentialCard, SkeletonCard } from "@/shared/ui/ui";
import { AdminGuard } from "@/features/admin/components";

const NO_PREFIX = { baseUrl: "" } as const;

async function getAccounts(): Promise<{ mx: BankAccount | null; us: UsBankAccount | null }> {
  const data = await authFetch(BANK_ACCOUNT_ROUTES.admin, bankAccountResponseSchema, NO_PREFIX);
  return { mx: data.bankAccount, us: data.usBankAccount ?? null };
}

async function saveAccount(input: BankAccount): Promise<BankAccount | null> {
  const data = await authFetch(BANK_ACCOUNT_ROUTES.admin, bankAccountResponseSchema, {
    ...NO_PREFIX,
    method: "PUT",
    body: input,
  });
  return data.bankAccount;
}

async function saveUsAccount(input: UsBankAccount): Promise<UsBankAccount | null> {
  const data = await authFetch(BANK_ACCOUNT_ROUTES.adminUsd, bankAccountResponseSchema, {
    ...NO_PREFIX,
    method: "PUT",
    body: input,
  });
  return data.usBankAccount ?? null;
}

const VACIA: BankAccount = { bankName: "", clabe: "", holderName: "" };
const VACIA_US: UsBankAccount = { bankName: "", holderName: "", accountNumber: "", routingNumber: "", address: "" };

/** Vacío → `undefined`, para que el esquema no guarde cadenas en blanco. */
function opcional(value: string | undefined): string | undefined {
  return value?.trim() ? value.trim() : undefined;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * LA CUENTA A LA QUE SE TRANSFIERE (Anahí, 29-ago).
 *
 * "Si eligen transferencia, muestra el número de cuenta a depositar."
 *
 * Existía la cuenta del FRANQUICIATARIO —a la que transfiere quien
 * compra en su tienda— pero no la de It's The Best, así que un pedido
 * normal con transferencia terminaba en "conserva tu comprobante" sin
 * decir a dónde mandar el dinero. Pedir una transferencia sin dar la
 * cuenta es pedirle a la clienta que escriba por WhatsApp para poder
 * pagar, y ahí es donde se caen las ventas.
 *
 * ── Por qué se edita aquí y no vive en el código ─────────────────────
 * Una CLABE cambia: otra cuenta, otro banco, un dígito corregido.
 * Escrita en el código, cambiarla exigiría un despliegue; desde aquí
 * surte efecto en el pedido siguiente.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminBankAccountPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const cuentas = useQuery({ queryKey: ["admin", "bank-account"], queryFn: getAccounts });
  const cuentaMx = cuentas.data?.mx ?? null;

  const [form, setForm] = useState<BankAccount>(VACIA);
  const [error, setError] = useState<string | null>(null);
  const [guardada, setGuardada] = useState(false);

  // La cuenta llega DESPUÉS del primer render: `useState` sola no la ve.
  useEffect(() => {
    if (cuentaMx) setForm({ ...cuentaMx, note: cuentaMx.note ?? "" });
  }, [cuentaMx]);

  const guardar = useMutation({
    mutationFn: saveAccount,
    onSuccess: () => {
      setGuardada(true);
      void queryClient.invalidateQueries({ queryKey: ["admin", "bank-account"] });
    },
  });

  function set<K extends keyof BankAccount>(key: K, value: BankAccount[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
    setGuardada(false);
  }

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);

    /*
     * Se valida con el MISMO esquema que el servidor: una CLABE de 17
     * dígitos tiene que rebotar aquí, no después de guardarla y de que
     * alguien transfiera a una cuenta que no existe.
     */
    const parsed = bankAccountSchema.safeParse({
      ...form,
      note: form.note?.trim() ? form.note.trim() : undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("Revisa los datos de la cuenta"));
      return;
    }
    guardar.mutate(parsed.data);
  }

  return (
    <AdminGuard>
      <PageHeader
        titleHidden
        title={t("Cuenta para transferencias")}
        description={t(
          "Es la cuenta que ve quien elige pagar por transferencia. Sin ella, su pedido no dice a dónde depositar.",
        )}
      />

      {/*
        ── LA MISMA card del perfil (Anahí, 29-ago) ────────────────────
        No una parecida: `CredentialCard` del design system, con su
        resplandor, su fila de estado y su pie. Es la tarjeta de "un dato
        de identidad que se guarda", y esta cuenta lo es — la de la casa.
        Rehacer una card blanca aparte era garantizar que dentro de un
        mes las dos se vieran distinto.
      */}
      {cuentas.isLoading ? (
        <SkeletonCard />
      ) : (
        <CredentialCard
          initials="IT"
          title={t("Cuenta para transferencias")}
          subtitle={t("It's The Best · a dónde depositan quienes pagan por transferencia")}
          statusLeft={
            <div className="flex items-center gap-2">
              <Landmark className="h-4 w-4 text-ds-violet-pop" />
              <span>{cuentaMx ? t("Cuenta registrada") : t("Sin cuenta registrada")}</span>
            </div>
          }
        >
          <form onSubmit={enviar} className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t("Banco")}
                value={form.bankName}
                onChange={(event) => set("bankName", event.target.value)}
                placeholder="BBVA"
              />
              <Input
                label={t("Titular")}
                value={form.holderName}
                onChange={(event) => set("holderName", event.target.value)}
                placeholder="It's The Best S.A. de C.V."
              />
            </div>
            <Input
              label={t("CLABE (18 dígitos)")}
              value={form.clabe}
              onChange={(event) => set("clabe", event.target.value.replace(/\D/g, ""))}
              placeholder="012180012345678901"
              inputMode="numeric"
              maxLength={18}
            />
            <Textarea
              label={t("Instrucciones (opcional)")}
              value={form.note ?? ""}
              onChange={(event) => set("note", event.target.value)}
              rows={2}
              placeholder={t("Usa tu folio como referencia y envía tu comprobante.")}
            />

            {error && (
              <Alert variant="error" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
            {guardada && <Alert variant="success">{t("Cuenta guardada.")}</Alert>}

            <div className="flex justify-end">
              <Button type="submit" loading={guardar.isPending}>
                {t("Guardar cuenta")}
              </Button>
            </div>
          </form>
        </CredentialCard>
      )}

      {cuentas.isLoading ? null : (
        <div className="mt-6">
          <UsAccountCard initial={cuentas.data?.us ?? null} />
        </div>
      )}
    </AdminGuard>
  );
}

/**
 * ── La cuenta de EE. UU. (29-sep-2026) ──────────────────────────────
 * La ve quien paga un pedido en USD por transferencia (ACH). Separada de
 * la CLABE porque se guarda y se valida distinto: número de cuenta +
 * routing de 9 dígitos con su dígito verificador.
 */
function UsAccountCard({ initial }: { initial: UsBankAccount | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UsBankAccount>(VACIA_US);
  const [error, setError] = useState<string | null>(null);
  const [guardada, setGuardada] = useState(false);

  useEffect(() => {
    if (initial) setForm({ ...initial, address: initial.address ?? "", note: initial.note ?? "" });
  }, [initial]);

  const guardar = useMutation({
    mutationFn: saveUsAccount,
    onSuccess: () => {
      setGuardada(true);
      void queryClient.invalidateQueries({ queryKey: ["admin", "bank-account"] });
    },
  });

  function set<K extends keyof UsBankAccount>(key: K, value: UsBankAccount[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
    setGuardada(false);
  }

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const parsed = usBankAccountSchema.safeParse({
      ...form,
      address: opcional(form.address),
      note: opcional(form.note),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("Revisa los datos de la cuenta"));
      return;
    }
    guardar.mutate(parsed.data);
  }

  return (
    <CredentialCard
      initials="US"
      title={t("Cuenta en Estados Unidos (USD)")}
      subtitle={t("La ven quienes pagan un pedido en dólares por transferencia (ACH)")}
      statusLeft={
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-ds-violet-pop" />
          <span>{initial ? t("Cuenta registrada") : t("Sin cuenta registrada")}</span>
        </div>
      }
    >
      <form onSubmit={enviar} className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t("Banco")}
            value={form.bankName}
            onChange={(event) => set("bankName", event.target.value)}
            placeholder="Bank of America"
          />
          <Input
            label={t("Titular")}
            value={form.holderName}
            onChange={(event) => set("holderName", event.target.value)}
            placeholder="ITS THE BEST LLC"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t("Número de cuenta")}
            value={form.accountNumber}
            onChange={(event) => set("accountNumber", event.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            maxLength={17}
          />
          <Input
            label={t("Routing number (9 dígitos)")}
            value={form.routingNumber}
            onChange={(event) => set("routingNumber", event.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            maxLength={9}
          />
        </div>
        <Input
          label={t("Dirección del titular (opcional)")}
          value={form.address ?? ""}
          onChange={(event) => set("address", event.target.value)}
        />
        <Textarea
          label={t("Instrucciones (opcional)")}
          value={form.note ?? ""}
          onChange={(event) => set("note", event.target.value)}
          rows={2}
          placeholder={t("Usa tu folio como referencia y envía tu comprobante.")}
        />

        {error && (
          <Alert variant="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {guardada && <Alert variant="success">{t("Cuenta guardada.")}</Alert>}

        <div className="flex justify-end">
          <Button type="submit" loading={guardar.isPending}>
            {t("Guardar cuenta")}
          </Button>
        </div>
      </form>
    </CredentialCard>
  );
}
